import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  Button,
  useColorModeValue,
  Card,
  CardBody,
  SimpleGrid,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stat,
  StatLabel,
  StatNumber,
  useToast,
  StatGroup,
  Divider,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PrivateNavbar } from "../../components/layout/PrivateNavbar";
import { Footer } from "../../components/layout/Footer";
import { userAPI, aiAPI, systemAPI } from "../../services";
import { useAuthStore } from "../../stores/authStore";

interface ReferralStats {
  affiliateId: string;
  referralsCount: number;
  totalEarnings: number;
  pendingEarnings: number;
  currencyPairs: {
    usd_brl: number;
  };
  referrals: Array<{
    email: string;
    firstName: string;
    lastName: string;
    createdAt: string;
  }>;
  recentEarnings: Array<{
    amount: number;
    status: string;
    source: string;
    createdAt: string;
  }>;
}

const CommissionTable = ({
  commissionSettings,
}: {
  commissionSettings: any;
}) => {
  const { t } = useTranslation();
  return (
    <Box overflowX="auto" w="100%">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>{t("profile.affiliate.tiers.level")}</Th>
            <Th>{t("profile.affiliate.tiers.requirement")}</Th>
            <Th>{t("profile.affiliate.tiers.commission")}</Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td>{t("profile.affiliate.tiers.standard")}</Td>
            <Td>{t("profile.affiliate.tiers.standardReq")}</Td>
            <Td>{(commissionSettings?.standard * 100).toFixed(0)}%</Td>
          </Tr>
          <Tr>
            <Td>{t("profile.affiliate.tiers.silver")}</Td>
            <Td>{t("profile.affiliate.tiers.silverReq")}</Td>
            <Td>{(commissionSettings?.silver * 100).toFixed(0)}%</Td>
          </Tr>
          <Tr>
            <Td>{t("profile.affiliate.tiers.gold")}</Td>
            <Td>{t("profile.affiliate.tiers.goldReq")}</Td>
            <Td>{(commissionSettings?.gold * 100).toFixed(0)}%</Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
};

export const Affiliate: React.FC = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { user, setUser } = useAuthStore();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currencyPairs, setCurrencyPairs] = useState<any>({ usd_brl: 5 });
  const tableBg = useColorModeValue("white", "gray.800");
  const [commissionSettings, setCommissionSettings] = useState<any>({
    affiliateEarnings: 0.5,
  });
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");

  useEffect(() => {
    const loadCurrencyPairs = async () => {
      try {
        const pairs = await systemAPI.getCurrencyPairs();
        setCurrencyPairs(pairs);
      } catch (error) {
        console.error("Error loading currency pairs:", error);
      }
    };
    loadCurrencyPairs();
    loadAffiliateCommissions();
    loadUserAffiliate();
  }, []);

  useEffect(() => {
    if (user?.affiliateId) {
      loadReferralStats();
    }
  }, [user?.affiliateId]);

  const loadUserAffiliate = async () => {
    const user = await userAPI.getCurrentUser();
    setUser(user);
  };

  const loadAffiliateCommissions = async () => {
    const affiliateCommissions = await systemAPI.getAffiliateCommissions();
    setCommissionSettings(affiliateCommissions);
  };

  const getBrlPrice = (value: number) => {
    return value * currencyPairs.usd_brl;
  };

  const loadReferralStats = async () => {
    try {
      const data = await userAPI.getReferralStats();
      setStats(data);
    } catch (error) {
      console.error("Error loading referral stats:", error);
    }
  };
  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <Heading
              size="xl"
              fontFamily="Nasalization"
              bgGradient="linear(to-r, brand.primary, brand.secondary)"
              bgClip="text"
            >
              {t("profile.affiliate.title")}
            </Heading>

            {!user?.affiliateId ? (
              <Card bg={cardBg}>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    <Text>{t("profile.affiliate.cardDescription")}</Text>
                    <Text>{t("profile.affiliate.cardDescription2")}</Text>
                    <CommissionTable commissionSettings={commissionSettings} />
                    <Button
                      colorScheme="brand"
                      onClick={async () => {
                        try {
                          const result = await userAPI.becomeAffiliate();
                          if (user) {
                            setUser({
                              ...user,
                              affiliateId: result.affiliateId,
                            });
                          }
                          toast({
                            title: t("profile.affiliate.success"),
                            status: "success",
                            duration: 3000,
                          });
                        } catch (error) {
                          toast({
                            title: t("profile.affiliate.error"),
                            status: "error",
                            duration: 3000,
                          });
                        }
                      }}
                    >
                      {t("profile.affiliate.join")}
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            ) : (
              <>
                <Card bg={cardBg}>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <Text>{t("profile.affiliate.cardDescription")}</Text>
                      <Text>{t("profile.affiliate.cardDescription2")}</Text>
                      <CommissionTable
                        commissionSettings={commissionSettings}
                      />

                      <Divider />
                      <Text>{t("profile.affiliate.yourId")}</Text>
                      <Input value={user.affiliateId} isReadOnly />
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(user!.affiliateId!);
                          toast({
                            title: t("profile.affiliate.copied"),
                            status: "success",
                            duration: 2000,
                          });
                        }}
                        colorScheme="brand"
                      >
                        {t("profile.affiliate.copy")}
                      </Button>
                      <Text>{t("profile.affiliate.link")}</Text>
                      <Input
                        value={`${window.location.origin}/?aff=${user.affiliateId}`}
                        isReadOnly
                      />
                      <Button
                        onClick={() => {
                          const link = `${window.location.origin}/?aff=${user.affiliateId}`;
                          navigator.clipboard.writeText(link);
                          toast({
                            title: t("profile.affiliate.linkCopied"),
                            status: "success",
                            duration: 2000,
                          });
                        }}
                        colorScheme="brand"
                      >
                        {t("profile.affiliate.copyLink")}
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>

                {stats && (
                  <>
                    <Card bg={cardBg}>
                      <CardBody>
                        <StatGroup
                          flexDirection={{ base: "column", md: "row" }}
                          gap={{ base: 4, md: 0 }}
                        >
                          <Stat>
                            <StatLabel>
                              {t("profile.affiliate.earnings.total")}
                            </StatLabel>
                            <StatNumber>
                              ${Number(stats.totalEarnings).toFixed(2)}
                              {i18n.language === "pt-BR" && (
                                <Text as="span" ml={2}>
                                  (R${" "}
                                  {getBrlPrice(
                                    Number(stats.totalEarnings)
                                  ).toFixed(2)}
                                  )
                                </Text>
                              )}
                            </StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>
                              {t("profile.affiliate.earnings.pending")}
                            </StatLabel>
                            <StatNumber>
                              ${Number(stats.pendingEarnings).toFixed(2)}
                              {i18n.language === "pt-BR" && (
                                <Text as="span" ml={2}>
                                  (R${" "}
                                  {getBrlPrice(
                                    Number(stats.pendingEarnings)
                                  ).toFixed(2)}
                                  )
                                </Text>
                              )}
                            </StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>
                              {t("profile.affiliate.earnings.referrals")}
                            </StatLabel>
                            <StatNumber>{stats.referralsCount}</StatNumber>
                          </Stat>
                        </StatGroup>
                      </CardBody>
                    </Card>

                    <Card bg={cardBg}>
                      <CardBody>
                        <VStack spacing={4} align="stretch">
                          <Heading size="md">
                            {t("profile.affiliate.earnings.recentEarnings")}
                          </Heading>
                          {stats.recentEarnings.length > 0 ? (
                            <Box
                              bg={tableBg}
                              shadow="base"
                              rounded="lg"
                              overflow="auto"
                            >
                              <Table variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th>
                                      {t("profile.affiliate.earnings.date")}
                                    </Th>
                                    <Th>
                                      {t("profile.affiliate.earnings.source")}
                                    </Th>
                                    <Th>
                                      {t("profile.affiliate.earnings.amount")}
                                    </Th>
                                    <Th>
                                      {t("profile.affiliate.earnings.status")}
                                    </Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {stats.recentEarnings.map(
                                    (earning, index) => (
                                      <Tr key={index}>
                                        <Td>
                                          {new Date(
                                            earning.createdAt
                                          ).toLocaleDateString(
                                            i18n.language === "pt-BR"
                                              ? "pt-BR"
                                              : "en-US"
                                          )}
                                        </Td>
                                        <Td>{earning.source}</Td>
                                        <Td>
                                          ${Number(earning.amount).toFixed(2)}
                                          {i18n.language === "pt-BR" && (
                                            <Text as="span" ml={2}>
                                              (R${" "}
                                              {getBrlPrice(
                                                Number(earning.amount)
                                              ).toFixed(2)}
                                              )
                                            </Text>
                                          )}
                                        </Td>
                                        <Td>{earning.status}</Td>
                                      </Tr>
                                    )
                                  )}
                                </Tbody>
                              </Table>
                            </Box>
                          ) : (
                            <Text>
                              {t("profile.affiliate.earnings.noEarnings")}
                            </Text>
                          )}
                        </VStack>
                      </CardBody>
                    </Card>
                  </>
                )}
              </>
            )}
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
